import { type Component, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import type { CompanyV2 } from "@/domains/company/company/api/types";
import { seedLocationRows } from "@/domains/company/company/utils/locations";
import { socialLinks } from "@/domains/company/public-page/utils/social";
import { isRenderableUrl } from "@/domains/company/public-page/utils/url";
import { AccordionSection } from "./accordion-section";

/**
 * Le sedi dell'azienda, una riga per città.
 *
 * Le righe vengono dalla **sola** lista strutturata: `seedLocationRows` cade su
 * `headquarters_location` quando quella lista è vuota, e qui quel ripiego
 * aprirebbe un «Sedi» con dentro la stessa identica sede che il blocco identità
 * stampa già in alto — la duplicazione appena tolta dalla riga «sede
 * principale» di Dettagli. Per il resto serve eccome: è lei che riduce «Milano,
 * Lombardia, Italia» alla sola città e scarta le voci senza città.
 *
 * Il gate è quindi sulle righe **sopravvissute**, non sulla lunghezza grezza di
 * `locations`: una voce come `{text: "   "}` è memorizzabile — solo `text` è
 * obbligatorio e l'import scrive senza validare — e contarla darebbe
 * un'intestazione «Sedi», un hint «1 città» e sotto una riga vuota.
 *
 * **La riga è la sola città, regione e paese non si stampano.** Quei campi
 * esistono solo sulle voci salvate dalla card Sedi: una voce scritta
 * dall'import porta la stessa informazione dentro `text` e nient'altro, quindi
 * stamparli quando ci sono farebbe apparire la stessa sede in due modi a
 * seconda di come è stata salvata — «Milano» contro «Milano · Lombardia,
 * Italia». Ricavare la regione spezzando `text` sarebbe l'altra uscita, ma
 * significherebbe leggere come regione la coda di una stringa di cui nessuno ha
 * dichiarato la struttura. La vetrina ha già scelto la città sola
 * (`locations-section.tsx`), ed è l'unica forma che le due sorgenti sanno
 * produrre allo stesso modo.
 */
export const LocationsSection: Component<{ company: CompanyV2 }> = (props) => {
  const { t } = useI18n();

  const rows = () =>
    props.company.locations?.length ? seedLocationRows(props.company) : [];

  return (
    <Show when={rows().length > 0}>
      <AccordionSection
        icon="ph-map-pin"
        title={t("joblist.detailSheet.company.locations.title")}
        hint={t("joblist.detailSheet.company.locations.hint", {
          count: rows().length,
        })}
      >
        <ul class="flex flex-col gap-2">
          <For each={rows()}>
            {(row) => (
              <li class="flex items-center gap-2 text-sm">
                <i
                  class="ph ph-map-pin shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span class="truncate font-medium">{row.city}</span>
                <Show when={row.headquarter}>
                  <Badge variant="secondary" size="sm" class="shrink-0">
                    {t("joblist.detailSheet.company.locations.headquarter")}
                  </Badge>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </AccordionSection>
    </Show>
  );
};

/**
 * La forma delle icone-link, la stessa della pagina pubblica.
 *
 * `transition-colors` senza `motion-reduce`, di proposito: a movimento ridotto
 * il colore resta — non è un movimento, è il feedback che dice che il bersaglio
 * è attivo.
 */
const SOCIAL_LINK_CLASS =
  "grid size-9 place-items-center rounded-md border border-border text-muted-foreground transition-colors duration-150 ease-out-strong hover:bg-accent hover:text-foreground";

/**
 * Dove l'azienda vive fuori di qui: LinkedIn e i social sopravvissuti.
 *
 * Due sorgenti, una sola icona LinkedIn: la colonna `linkedin_url` la porta,
 * mentre `socialLinks` scarta da sé la chiave `linkedin` rimasta nel jsonb dopo
 * la migrazione — quindi anche con entrambe popolate non può uscirne un
 * doppione. Quella funzione fa anche il resto del lavoro sporco (sanifica,
 * valida, ordina, e dà un glifo generico alle chiavi che non conosce), e per
 * `linkedin_url` lo stesso giudizio lo dà `isRenderableUrl`: «@azienda» non è un
 * indirizzo, e un `href` relativo porterebbe dentro la nostra app.
 *
 * Se non sopravvive **nessun** link la sezione non esiste: un'intestazione con
 * dentro zero icone prometterebbe qualcosa che non c'è.
 */
export const SocialSection: Component<{ company: CompanyV2 }> = (props) => {
  const { t } = useI18n();

  const socials = () => socialLinks(props.company.social_media);
  const linkedin = () =>
    isRenderableUrl(props.company.linkedin_url)
      ? props.company.linkedin_url
      : undefined;

  return (
    <Show when={socials().length > 0 || linkedin()}>
      <AccordionSection
        icon="ph-share-network"
        title={t("joblist.detailSheet.company.social.title")}
      >
        <div class="flex flex-wrap items-center gap-1">
          <Show when={linkedin()}>
            {(url) => (
              <a
                class={SOCIAL_LINK_CLASS}
                href={url()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="ph ph-linkedin-logo" aria-hidden="true" />
                {/* `socialLabel` e non `identity.linkedin`: in questa fila le
                    icone sono tutte mute, e la sola etichetta scritta in
                    un'altra forma («LinkedIn» contro «Profilo github») fa
                    sentire a chi non vede due cose diverse dove ce n'è una
                    sola. Sulla vetrina LinkedIn è un bottone con testo
                    visibile, quindi lì la forma corta resta giusta. */}
                <span class="sr-only">
                  {t("company.publicPage.identity.socialLabel", {
                    network: "LinkedIn",
                  })}
                </span>
              </a>
            )}
          </Show>
          <For each={socials()}>
            {(social) => (
              <a
                class={SOCIAL_LINK_CLASS}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class={`ph ${social.icon}`} aria-hidden="true" />
                <span class="sr-only">
                  {t("company.publicPage.identity.socialLabel", {
                    network: social.key,
                  })}
                </span>
              </a>
            )}
          </For>
        </div>
      </AccordionSection>
    </Show>
  );
};
